import React, { useEffect, useState } from "react";

import { api } from "../../api/client";
import { OutlineButton, PrimaryButton } from "../ui/Buttons";


export default function AccountModal({ open, onClose, user, token, onAfterUpdate, initialPlan = "basic" }) {
  const [plan, setPlan] = useState(initialPlan);
  const [busy, setBusy] = useState(false);
  const [paddleEnabled, setPaddleEnabled] = useState(false);

  useEffect(() => { setPlan(initialPlan); }, [initialPlan]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await api.get("/payments/paddle/config");
        if (!cancelled) setPaddleEnabled(!!r.data?.enabled);
      } catch (e) { if (!cancelled) setPaddleEnabled(false); }
    }
    if (open) load();
    return () => { cancelled = true; };
  }, [open]);

  async function subscribe() {
    try {
      setBusy(true);
      if (!paddleEnabled) { window.alert("Payments not configured yet"); return; }
      const cfg = await api.get("/payments/paddle/config");
      const env = (cfg.data?.environment || "sandbox").toLowerCase();
      const tokenResp = await api.get("/payments/paddle/client-token");
      const clientToken = tokenResp.data?.token;
      const basicId = cfg.data?.basic_price_id;
      const proId = cfg.data?.pro_price_id;
      const priceId = plan === "basic" ? basicId : proId;
      if (!clientToken || !priceId) { window.alert("Checkout not configured"); return; }
      const ensureOverlay = async () => {
        if (!window.Paddle) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }
        // Wait a moment for Paddle to fully initialize
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          // Set environment for sandbox mode only (before Initialize)
          if (env === "sandbox" && window.Paddle.Environment) {
            window.Paddle.Environment.set("sandbox");
          }
          // Initialize Paddle
          window.Paddle.Initialize({ 
            token: clientToken,
            eventCallback: function(event) {
              console.log('Paddle event:', event);
            }
          });
          const uid = (localStorage.getItem("auth_user") ? JSON.parse(localStorage.getItem("auth_user")).id : "");
          try { localStorage.setItem("awaiting_payment", "1"); } catch (_e) { /* noop */ }
          if (window.Paddle.on) {
            try {
              window.Paddle.on("checkout.completed", () => {
                try { localStorage.removeItem("awaiting_payment"); } catch (_e) { /* noop */ }
                try { if (onAfterUpdate) onAfterUpdate(); } catch (_e) { /* noop */ }
              });
              window.Paddle.on("checkout.closed", () => { try { localStorage.removeItem("awaiting_payment"); } catch (_e) { /* noop */ } });
            } catch (_e) { /* noop */ }
          }
          window.Paddle.Checkout.open({
            items: [{ priceId }],
            customer: { email: user?.email },
            customData: { user_id: uid },
            settings: { successUrl: window.location.origin + "/app?paid=1" },
          });
        } catch (err) {
          window.alert("Could not open checkout: " + String(err));
        }
      };
      await ensureOverlay();
    } catch (e) {
      window.alert("Subscribe failed: " + (e?.response?.data?.detail || String(e)));
    } finally { setBusy(false); }
  }

  async function cancelSub() {
    if (!window.confirm("Are you sure you want to cancel your subscription? Your access will continue until the end of the billing period.")) {
      return;
    }
    try {
      setBusy(true);
      const r = await api.post("/payments/paddle/cancel-paddle-subscription", {});
      if (onAfterUpdate) onAfterUpdate();
      const message = r.data?.message || "Subscription canceled";
      window.alert(message);
    } catch (e) {
      window.alert("Cancel failed: " + (e?.response?.data?.detail || String(e)));
    } finally { setBusy(false); }
  }

  async function cancelSubLocal() {
    try {
      setBusy(true);
      await api.post("/payments/paddle/cancel-subscription", {});
      if (onAfterUpdate) onAfterUpdate();
      window.alert("Subscription deactivated locally (for testing)");
    } catch (e) {
      window.alert("Cancel failed: " + (e?.response?.data?.detail || String(e)));
    } finally { setBusy(false); }
  }

  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Account</div>
            <div className="subtle text-sm">{user?.email}</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="row">
            <div>
              <div className="label">Subscription</div>
              <div className="flex items-center gap-8 mt-2">
                <div className={`badge ${user?.subscription_active ? "badge-active" : "badge-idle"}`}>
                  {user?.subscription_active ? "Active" : "Inactive"}
                </div>
                <div className="plan-selector">
                  <button className={`chip ${plan==="basic"?"chip-on":""}`} onClick={()=>setPlan("basic")}>Basic</button>
                  <button className={`chip ${plan==="pro"?"chip-on":""}`} onClick={()=>setPlan("pro")}>Pro</button>
                </div>
                <PrimaryButton disabled={busy} onClick={subscribe} data-testid="subscribe-btn">Subscribe</PrimaryButton>
                {user?.subscription_active && (
                  <>
                    <OutlineButton className="ml-2" disabled={busy} onClick={cancelSub} data-testid="cancel-subscription-btn">Cancel Subscription</OutlineButton>
                    <button className="link ml-2 text-sm" disabled={busy} onClick={cancelSubLocal} data-testid="cancel-local-btn">(Test: deactivate locally)</button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="label">Buy credits</div>
            <div className="packs">
              <button className="pack disabled" disabled>25 credits</button>
              <button className="pack disabled" disabled>50 credits</button>
              <button className="pack disabled" disabled>100 credits</button>
            </div>
            <div className="subtle text-xs mt-2">Disabled until Paddle price IDs are provided</div>
          </div>

        </div>
      </div>
    </div>
  );
}

